import { components as c } from "@/components";
import { FormSelectDialog } from "@/components/form/FormSelectDialog";
import { ScheduleSelectDialog } from "@/components/form/ScheduleSelectDialog";
import { useSelectForms } from "@/features/forms/api/selectForms";
import RequestForm from "@/features/forms/components/RequestForm";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateStore } from "@/features/templates/stores/useTemplateStore";
import { templateSettingsSchema } from "@/types/zod/quickstart-settings";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CopyIcon, PlusIcon, TrashIcon } from "lucide-react";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import type { z } from "zod";

type TrackerFormValues = Pick<
	z.infer<typeof templateSettingsSchema>,
	"trackers"
>;

const TrackerFormPage = () => {
	const { data: forms = [] } = useSelectForms();
	const { data: schedules = [] } = useSchedulesQuery();
	const { trackers, setTrackers, addTracker, removeTracker } =
		useTemplateStore();
	const { markStepComplete, nextStep, previousStep, setCurrentStep } =
		useTemplateNavigationStore();
	const navigate = useNavigate();

	useEffect(() => {
		setCurrentStep("trackers");
	}, [setCurrentStep]);

	const form = useForm<TrackerFormValues>({
		resolver: zodResolver(
			templateSettingsSchema.pick({
				trackers: true,
			}),
		),
		defaultValues: {
			trackers: trackers.length > 0
				? trackers
				: [{ form_id: "", schedule_id: "" }],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: "trackers",
	});

	useEffect(() => {
		const subscription = form.watch((value) => {
			if (value.trackers) {
				setTrackers(value.trackers);
			}
		});
		return () => subscription.unsubscribe();
	}, [form, setTrackers]);

	const handleAddTracker = () => {
		const newTracker = { form_id: "", schedule_id: "" };
		append(newTracker);
		addTracker(newTracker);
	};

	const handleRemoveTracker = (index: number) => {
		remove(index);
		removeTracker(index);
	};

	const handleDuplicateTracker = (index: number) => {
		const trackerToDuplicate = trackers[index];
		if (trackerToDuplicate) {
			append(trackerToDuplicate);
			addTracker(trackerToDuplicate);
		}
	};

	const handleNext = () => {
		if (trackers.every((t) => t.form_id && t.schedule_id)) {
			markStepComplete("trackers");
			nextStep();
			navigate({ to: "/new/diary/$templateId/events" });
		}
	};

	const handlePrevious = () => {
		previousStep();
		navigate({ to: "/new/diary/$templateId/organisation" });
	};

	return (
		<div className="space-y-8">
			<c.ui.Form {...form}>
				<div className="space-y-4">
					{fields.map((field, index) => (
						<c.ui.Card key={field.id}>
							<c.ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
								<c.ui.CardTitle className="text-base">
									Data Collection {index + 1}
								</c.ui.CardTitle>
								<div className="flex gap-2">
									<c.ui.Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() =>
											handleDuplicateTracker(index)}
									>
										<CopyIcon className="w-4 h-4" />
									</c.ui.Button>
									<c.ui.Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() =>
											handleRemoveTracker(index)}
										disabled={index === 0 &&
											fields.length === 1}
									>
										<TrashIcon className="w-4 h-4" />
									</c.ui.Button>
								</div>
							</c.ui.CardHeader>
							<c.ui.CardContent className="flex md:flex-row flex-col items-end gap-4">
								{/* Tracker Selection */}
								<div className="flex-grow w-full md:max-w-[50%] md:basis-1/2">
									<FormSelectDialog
										name={`trackers.${index}.form_id`}
										control={form.control}
										options={forms.filter((f) =>
											f.is_published
										)}
										addOption={<RequestForm />}
										label={index === 0
											? "Select Questionnaire"
											: undefined}
										description={index === 0
											? "What questions should the patient answer?"
											: undefined}
									/>
								</div>

								{/* Schedule Selection */}
								<div className="flex-grow w-full md:max-w-[50%] md:basis-1/2">
									<ScheduleSelectDialog
										name={`trackers.${index}.schedule_id`}
										control={form.control}
										options={schedules?.filter((s) =>
											s.is_published
										) ?? []}
										label={index === 0
											? "Select Schedule"
											: undefined}
										description={index === 0
											? (
												<>
													When should the patient
													complete the
													questionnaire?{" "}
												</>
											)
											: undefined}
									/>
								</div>
							</c.ui.CardContent>
						</c.ui.Card>
					))}
				</div>
				<c.ui.Button
					type="button"
					variant="secondary"
					className="w-full"
					onClick={handleAddTracker}
				>
					<PlusIcon className="mr-2 w-4 h-4" />
					Collect Additional Data
				</c.ui.Button>
			</c.ui.Form>

			{/* Navigation Buttons */}
			<div className="flex justify-between gap-4 mt-8">
				<c.ui.Button
					type="button"
					variant="outline"
					onClick={handlePrevious}
				>
					Previous
				</c.ui.Button>

				<c.ui.Button
					type="button"
					onClick={handleNext}
					disabled={!trackers.every((t) =>
						t.form_id && t.schedule_id
					)}
				>
					Next
				</c.ui.Button>
			</div>
		</div>
	);
};

export const Route = createFileRoute("/_app/new/diary/$templateId/trackers")({
	component: TrackerFormPage,
});
